/*
 * File: en-US.ts
 * File Created: 2023-11-27 03:22:24
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-03-12 10:10:40
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { RegisterLanguage } from "@root/core/i18n";

@RegisterLanguage("en-US", "English")
export default class LangObject {
    /// copyright
    w_Copyright = "HCU Tech 2025 © All rights reserved.";

    /// Login
    w_RememberMe = "Remember Me";
    w_ForgetPassword = "Forget Password";

    /// Menu
    w_UserManagement = "User Management";
    w_UserList = "User";
    w_MenuLoginLog = "Login Log";
    w_MenuOrgAndMember = "Organizations & Members";
    w_MenuOrgList = "Organizations";
    w_MenuMembership = "Memberships";
    w_MenuBilling = "Billing & Payments";
    w_MenuFinanceCredits = "Finance & Credits";
    w_MenuWallet = "Wallets";
    w_MenuLedger = "Ledgers";
    w_MenuPromoAndRedeem = "Promo & Redeem";
    w_MenuPromoCode = "Promo Codes";
    w_MenuRedeemCode = "Redeem Codes";
    w_MenuRedemption = "Redemptions";
    w_MenuTranslation = "File Translation";
    w_MenuTranslateTask = "Translation Tasks";
    w_MenuTranslateTaskContent = "Task Details";

    /// Maintenance
    w_MaintenanceStatus = "SYSTEM MAINTENANCE";
    w_MaintenanceTitle = "System Maintenance in Progress";
    w_MaintenanceDesc =
        "We are currently undergoing system maintenance to enhance your QUAFF AI experience. Services will be temporarily unavailable during this time. We apologize for any inconvenience and appreciate your patience.";
    w_MaintenanceEtaLabel = "Estimated Restoration Time";
    w_MaintenanceRefresh = "Refresh";
    w_MaintenanceSupport = "Need help? Contact our support team.";
    w_MaintenanceTBD = "TBD";
    w_MaintenanceEmailSubject = "QUAFF AI Maintenance Support";

    /// Invoice
    w_InvoiceInfo = "Invoice Information";
    w_InvoiceTypePersonal = "Personal";
    w_InvoiceTypeCompany = "Company";
    w_InvoiceTypeOverseas = "Non-Taiwan user";
    w_InvoiceEmail = "Email";
    w_InvoiceCarrier = "Mobile Carrier";
    w_InvoiceCompanyName = "Company Name";
    w_InvoiceTaxId = "Tax ID";
    w_InvoiceEmailRequired = "Email is required";
    w_InvoiceTypeRequired = "Please select an invoice type";
    w_InvoiceCompanyNameRequired = "Company name is required";
    w_InvoiceTaxIdInvalid = "Tax ID must be 8 digits";
    w_InvoiceTaxIdInvalidChecksum = "Tax ID is invalid";
    w_MenuTranslateError = "Failed Jobs";
    w_MenuAIServices = "AI Services";

    /// Title
    w_TitleRelatedAccount = "Related Account";

    /// Header
    w_HeaderRelation = "Relation";
    w_HeaderAdditionalCondition = "Additional Conditions";

    /// View
    // User
    w_UserPageDescription = "Manage users, permissions, and status.";
    w_UserCreateNew = "Create New User";
    w_UserRoleAdmin = "Admin";
    w_UserRoleBillingAdmin = "Billing Admin";
    w_UserRoleUser = "User";
    w_UserProfile = "Profile";
    w_UserAccounts = "Related Accounts";
    w_UserResetPassword = "Reset Password";
    w_UserName = "Username";
    w_UserInfo = "User Info";
    w_UserRelationSummary = "Relations";
    w_UserLinkNewAccount = "Link New Account";
    w_UserLinkedAccounts = "Linked Accounts";
    w_UserUnlinkAccount = "Unlink";
    w_UserLinkedOrganizations = "Organizations";
    w_UserOwnedOrganizations = "Owned Organizations";
    w_UserCreateOrganization = "Create Organization";
    w_UserBelongOrganizations = "Belong to Organizations";
    w_UserMemberships = "Memberships";
    w_UserMembershipsNote =
        "Organizations where this user is a member but not the owner.";
    w_UserOrganizations = "Related Organizations";

    /// Payment Success
    w_PaymentSuccess_TitleSuccess = "Payment successful!";
    w_PaymentSuccess_TitleFailed = "Payment failed";
    w_PaymentSuccess_DescSuccess =
        "Thank you for your top-up. We have received your payment and credited your organization wallet.";
    w_PaymentSuccess_DescFailed =
        "This payment was not completed. If you were charged, please contact support and provide the order number.";
    w_PaymentSuccess_LabelMerchantTradeNo = "Order No.";
    w_PaymentSuccess_LabelTradeNo = "Trade No.";
    w_PaymentSuccess_LabelPaymentType = "Payment Method";
    w_PaymentSuccess_LabelPaymentDate = "Payment Time";
    w_PaymentSuccess_LabelAmount = "Credited Points";
    w_PaymentSuccess_LabelPaidAmount = "Paid Amount (TWD)";
    w_PaymentSuccess_LabelDiscount = "Discount (TWD)";
    w_PaymentSuccess_LabelRtnMsg = "Transaction Message";
    w_PaymentSuccess_PaymentTypeDefault = "Credit Card (ECPay)";
    w_PaymentSuccess_Close = "Close this window";
    w_PaymentSuccess_AutoClose = "This window will close automatically in {0} seconds...";
    w_PaymentSuccess_CloseManual = "Please close this tab manually";
    w_PaymentSuccess_CloseManualDesc =
        "Security restriction: the browser cannot close this tab automatically. Please close it manually.";

    /// Topup Discount
    w_TranslationJobTopupDiscountEmpty = "Please enter a discount code.";
    w_TranslationJobTopupDiscountNotFound = "Discount code not found.";
    w_TranslationJobTopupDiscountDisabled = "Discount code is disabled.";
    w_TranslationJobTopupDiscountNotStarted = "Discount code has not started yet.";
    w_TranslationJobTopupDiscountExpired = "Discount code has expired.";
    w_TranslationJobTopupDiscountMaxUses = "Discount code has reached its usage limit.";
    w_TranslationJobTopupDiscountNotDiscount = "This code cannot be used for discounts.";
    w_TranslationJobTopupDiscountMinSpend = "Minimum spend requirement not met ({0}).";
    w_TranslationJobTopupDiscountOrgLimit =
        "Your organization has reached the usage limit.";
    w_TranslationJobTopupDiscountInvalid = "Invalid discount code.";
    w_TranslationJobTopupDiscountApplied = "Discount code applied";

    // Account
    w_AccountLocalName = "{0} (Local Account)";
    w_AccountLdapName = "{0} (LDAP Account)";
    w_AccountGoogleName = "{0} (Google Account)";
    w_AccountFieldProvider = "Provider";
    w_AccountFieldProviderId = "Account ID / Email";
    w_AccountProviderCredentials = "Local Credentials";
    w_AccountProviderLdap = "LDAP";
    w_AccountProviderGoogle = "Google";
    w_AccountLinkedAt = "Linked At";

    // Organization
    w_OrganizationGeneralName = "Organization Name";
    w_OrganizationRoleOwner = "Owner";
    w_OrganizationPersonalName = "{0}'s Personal Space";
    w_OrganizationOrgName = "{0}'s Organization";
    w_OrganizationTypePersonal = "Personal";
    w_OrganizationTypeOrg = "Organization";
    w_OrganizationOwner = "Organization Owner";
    w_OrganizationMembers = "Related Members";
    w_OrganizationBelongTo = "Belongs to Organization";

    // Membership
    w_MembershipUser = "Member";
    w_MembershipPersonalName = "Membership of {w_OrganizationPersonalName('{0}')}";
    w_MembershipOrgName = "Membership of {0}";
    w_MembershipCustomName = "Membership of {0}";
    w_MembershipRoleAdmin = "Administrator";
    w_MembershipRoleMember = "Member";

    // Wallet
    w_WalletPersonalName = "Wallet of {w_OrganizationPersonalName('{0}')}";
    w_WalletOrgName = "Wallet of {0}";
    w_WalletBalance = "Balance";
    w_WalletLedgerCount = "Ledger Count";

    // Ledger
    w_LedgerDescription = "Track balance changes, deposits, refunds, and usage records.";
    w_LedgerName = "Transaction";
    w_LedgerNameId = "Transaction / ID";
    w_LedgerReason = "Reason";
    w_LedgerSource = "Type";
    w_LedgerDelta = "Points";
    w_LedgerId = "ID";
    w_LedgerFilterAll = "All Types";
    w_LedgerExportCsv = "Export CSV";
    w_LedgerSourceDeposit = "Top-up";
    w_LedgerSourcePromoCredit = "Promo Credit";
    w_LedgerSourceUsageDebit = "Usage";
    w_LedgerSourceRefund = "System Refund";
    w_LedgerSourceInitialCredit = "Initial Credit";
    w_LedgerReasonInitialCredit = "Initial Credit";
    w_LedgerReasonPayuniTopup = "PayUni Top-up";
    w_LedgerReasonPromoRedeem = "Redeem Promo Code";
    w_LedgerReasonTranslationDebit = "Translation Debit";
    w_LedgerReasonTranslationRefund = "Translation Refund";
    w_LedgerAmountRefunded = "Refunded";

    // PromoCode
    w_PromoCodeDescription = "Manage credit vouchers and discount coupons.";
    w_PromoCodeCreateNew = "New Code";
    w_PromoCodeSearchPlaceholder = "Search by name...";
    w_PromoCodeFilterAll = "All Types";
    w_PromoCodeTypeValue = "Type & Value";
    w_PromoCodeLimit = "Limits";
    w_PromoCodeLimitTotal = "Total Limit:";
    w_PromoCodeLimitOrg = "Per Org Limit:";
    w_PromoCodeNote = "Internal Note";
    w_PromoCodeTypeConfig = "Type Configuration";
    w_PromoCodeLimitsValidity = "Limits & Validity";
    w_PromoCodeKind = "Promo Type";
    w_PromoCodeName = "Code Name";
    w_PromoCodeNameDescription = "Example: QUAFFPROMOTE_2026";
    w_PromoCodeBenefit = "Benefit Details";
    w_PromoCodeEnableStatus = "Enable Status";
    w_PromoCodeTimeRange = "Promo Time Range";
    w_PromotionCodeCredit = "Credit Code";
    w_PromotionCodeDiscount = "Discount Code";
    w_PromotionCodeDiscountType = "Discount Type";
    w_PromotionCodeDiscountTypePercent = "Percent";
    w_PromotionCodeDiscountTypeAmount = "Amount";
    w_PromoCodeCreditAmount = "Credit Amount";
    w_PromotionMinSpendAmount = "Minimum Spend Amount";
    w_PromotionMaxUses = "Max Uses"; // en-US
    w_PromotionUsedCount = "Used Count"; // en-US
    w_PromotionPerOrgLimit = "Per-Organization Limit"; // en-US

    // TranslationJob
    w_TranslationJobDescription = "Monitor progress and download translated files.";
    w_TranslationJobCreateNew = "New Translation";
    w_TranslationJobAnalyzeStart = "Start Analysis";
    w_TranslationJobAnalyzing = "Analyzing...";
    w_TranslationJobConverting = "Converting...";
    w_TranslationJobConvertFailed = "Convert failed. Please try again.";
    w_TranslationJobAnalyzeBadZip =
        "The file is corrupted or has an invalid format. Please check the file.";
    w_TranslationJobAnalyzeNoTextTitle = "No text detected";
    w_TranslationJobAnalyzeNoTextDesc =
        "This file doesn’t seem to contain any text to translate.";
    w_TranslationJobAnalyzeFailed = "Analysis failed. Please try again.";
    w_TranslationJobAnalyzeStepUpload = "Upload File & Language";
    w_TranslationJobFile = "File";
    w_TranslationJobSourceLang = "Source Language";
    w_TranslationJobTargetLang = "Target Language";
    w_TranslationJobTokens = "Token Count";
    w_TranslationJobWords = "Word Count";
    w_FileKindWord = "Word";
    w_FileKindExcel = "Excel";
    w_FileKindPowerPoint = "PowerPoint";
    w_TranslationJobPages = "Page Count";
    w_TranslationJobBalanceLabel = "Balance:";
    w_TranslationJobTopup = "Top Up Credits";
    w_TranslationJobTopupTitle = "Add Funds";
    w_TranslationJobTopupTarget = "To:";
    w_TranslationJobTopupOnline = "Online Payment";
    w_TranslationJobTopupRedeem = "Redeem Code";
    w_TranslationJobTopupSelectAmount = "Select Amount (TWD)";
    w_TranslationJobTopupPopular = "Popular";
    w_TranslationJobTopupCustomAmount = "Custom amount...";
    w_TranslationJobTopupDiscount = "Discount Code";
    w_TranslationJobTopupDiscountPlaceholder = "e.g. SAVE10";
    w_TranslationJobTopupApply = "Apply";
    w_TranslationJobTopupSubtotal = "Subtotal";
    w_TranslationJobTopupDiscountLabel = "Discount";
    w_TranslationJobTopupTotal = "Total to Pay";
    w_TranslationJobTopupCreditPoints = "Credit Points";
    w_TranslationJobTopupPay = "Pay with Credit Card";
    w_TranslationJobTopupSecure = "Secure payment processed by ECPay";
    w_TranslationJobTopupAgreeTerms = "I have read and agree to the";
    w_TranslationJobTopupTerms = "Terms & Refund Policy";
    w_TranslationJobTopupCardNumber = "Card Number";
    w_TranslationJobTopupCardNumberPlaceholder = "1234 5678 9012 3456";
    w_TranslationJobTopupCardExpired = "Expiry (MMYY)";
    w_TranslationJobTopupCardExpiredPlaceholder = "MMYY";
    w_TranslationJobTopupCardCvc = "CVC";
    w_TranslationJobTopupCardCvcPlaceholder = "123";
    w_TranslationJobTopupVoucher = "Voucher Code";
    w_TranslationJobTopupVoucherPlaceholder = "e.g. SPRING2026";
    w_TranslationJobTopupVoucherHint = "Enter your promo code to redeem points.";
    w_TranslationJobTopupRedeemButton = "Redeem Code";
    w_TranslationJobTopupRedeemEmpty = "Please enter a promo code.";
    w_TranslationJobTopupRedeemSuccess = "Redeemed successfully. {0} points added.";
    w_TranslationJobTopupRedeemFailed = "Redeem failed. Please try again.";
    w_TranslationJobTopupRedeemNotFound = "Promo code not found.";
    w_TranslationJobTopupRedeemDisabled = "Promo code is disabled.";
    w_TranslationJobTopupRedeemNotStarted = "Promo code not started yet.";
    w_TranslationJobTopupRedeemExpired = "Promo code has expired.";
    w_TranslationJobTopupRedeemMaxUses = "Promo code has reached max uses.";
    w_TranslationJobTopupRedeemNotCredit =
        "This promo code cannot be redeemed for points.";
    w_TranslationJobTopupRedeemInvalid = "Promo code is invalid.";
    w_TranslationJobTopupRedeemOrgLimit =
        "This promo code has reached the organization limit.";
    w_TranslationJobTopupRedeemOrgNotFound = "Organization not found for this account.";
    w_TranslationJobTopupRedeemWalletNotFound = "Wallet not found for this organization.";
    w_TranslationJobTopupPayuniNotConfigured = "Payment service is not configured.";
    w_TranslationJobTopupAmountInvalid = "Invalid top up amount.";
    w_TranslationJobTopupCardInvalid = "Invalid card information.";
    w_TranslationJobTopupOrgNotFound = "Organization not found for this account.";
    w_TranslationJobTopupWalletNotFound = "Wallet not found for this organization.";
    w_TranslationJobTopupPayuniRequestFailed = "Payment request failed.";
    w_TranslationJobTopupPayuniResponseInvalid = "Payment response invalid.";
    w_TranslationJobTopupPayuniHashInvalid = "Payment response verification failed.";
    w_TranslationJobTopupPayuniIframeDomainMissing =
        "Payment iframe domain is not configured.";
    w_TranslationJobTopupPayuniSdkUrlMissing = "Payment SDK URL is not configured.";
    w_TranslationJobTopupPayuniSdkTokenFailed = "Failed to initialize payment.";
    w_TranslationJobTopupPayuniTokenMissing = "Payment token missing.";
    w_TranslationJobTopupPayuniUnknown = "Payment pending. Please check later.";
    w_TranslationJobTopupPayuniUnapproved = "Payment pending approval.";
    w_TranslationJobTopupPayuniFailed = "Payment failed.";
    w_TranslationJobTopupPayuniTradeNotPaid = "Payment not completed.";
    w_TranslationJobTopupPayuniSuccess = "Payment successful. Wallet updated.";
    w_TranslationJobTopupPayuniInit = "Initializing payment widget...";
    w_TranslationJobOwnedOrganizations = "Owned Organizations";
    w_TranslationJobMemberships = "Memberships";
    w_TranslationJobTaskFile = "Task Name / File";
    w_TranslationJobLanguages = "Languages";
    w_TranslationJobStatusProgress = "Status & Progress";
    w_TranslationJobCost = "Cost";
    w_TranslationJobActions = "Actions";
    w_FailedJobDescription =
        "Review failed translation jobs, download the original files, and inspect error details.";
    w_FailedJobUserOrg = "User / Organization";
    w_FailedJobFile = "File";
    w_FailedJobError = "Error";
    w_FailedJobFailedAt = "Failed At";
    w_FailedJobActions = "Actions";
    w_FailedJobDownloadSource = "Download Source";
    w_FailedJobFilterAllTypes = "All types";
    w_FailedJobFilterCompleted = "Completed";
    w_FailedJobFilterTimeout = "Timeout";
    w_FailedJobFilterUnsupported = "Unsupported";
    w_FailedJobFilterResolved = "Resolved";
    w_FailedJobFilterCritical = "Pending";
    w_CompanyName = "HCU Tech Co. Ltd.";
    w_CompanyTaxId = "Tax ID: 55974998";
    w_TranslationJobDownload = "Download";
    w_ContactUs = "Contact us";
    w_TranslationJobStatusQueued = "Queued";
    w_TranslationJobStatusStarting = "Starting";
    w_TranslationJobStatusProcessing = "Translating";
    w_TranslationJobStatusCompleted = "Completed";
    w_TranslationJobStatusFailed = "Failed";
    w_TranslationJobFailDetailTitle = "Task Failure Details";
    w_TranslationJobFailFileName = "File name";
    w_TranslationJobFailReason = "Error reason";
    w_TranslationJobFailErrorMessage = "Error message";
    w_TranslationJobFailSuggestion = "Suggested action";
    w_TranslationJobFailReasonTimeoutTitle = "Translation timed out";
    w_TranslationJobFailReasonTimeoutDetail =
        "The translation process took longer than expected. Please wait a moment and try again.";
    w_TranslationJobFailReasonCriticalTitle = "Unable to translate file";
    w_TranslationJobFailReasonCriticalDetail =
        "We encountered an unexpected error with this file. An error report has been sent to our team, but unfortunately, retrying the exact same file won't work.";
    w_TranslationJobFailReasonUnsupportedTitle = "File format not supported";
    w_TranslationJobFailReasonUnsupportedDetail =
        "We currently don't support this file type. Please use a supported format, as retrying with this file won't succeed.";
    w_TranslationJobFailSuggestionRetryLine =
        "Please wait a few minutes before trying again.";
    w_TranslationJobFailSuggestionStopLine =
        "Since this issue cannot be resolved right now, retrying will unfortunately result in the same error.";
    w_TranslationJobFailSuggestionFixFormatLine =
        "Try saving your document as a standard Office file, or remove any complex formatting before trying again.";
    w_TranslationJobResolve = "Resolve";
    w_TranslationJobResolveTitle = "Resolution";
    w_TranslationJobResolveMessage = "Resolution notes";
    w_TranslationJobResolvePlaceholder = "Enter resolution notes";
    w_TranslationJobResolveSubmit = "Submit";
    "Try saving your document as a standard Office file, or remove any complex formatting before trying again.";
    w_TranslationJobUploadTitle = "Drag & Drop file here";
    w_TranslationJobUploadHint =
        "Supported formats: .doc, .docx, .xls, .xlsx, .ppt, .pptx (Max {0})";
    w_TranslationJobBrowseFiles = "Browse Files";
    w_TranslationJobFileSelected = "File selected successfully";
    w_TranslationJobChangeFile = "Change File";
    w_TranslationJobStepUpload = "Upload & Settings";
    w_TranslationJobStepEstimate = "Estimate & Confirm";
    w_TranslationJobDetected = "Detected";
    w_TranslationJobSize = "Size";
    w_TranslationJobReady = "Ready";
    w_TranslationJobEstimatedWords = "Estimated Word Count";
    w_TranslationJobEstimatedCost = "Estimated Points";
    w_TranslationJobWordsUnit = "words";
    w_PointUnit = "pts";
    w_TranslationJobEstimateNotice =
        "Analysis is an estimate. Actual cost may vary slightly based on final token count.";
    w_TranslationJobConfirmNotice =
        "Token count has been calculated. Confirm to create the translation job.";
    w_TranslationJobConfirmInsufficient =
        "Insufficient balance. Please top up to continue.";
    w_TranslationJobTopupNow = "Top Up Now";
    w_TranslationJobConfirmWalletNotFound = "Wallet not found for this organization.";
    w_TranslationJobWalletBalance = "Wallet Points";
    w_TranslationJobBalanceAfter = "Balance After (pts)";
    w_TranslationJobNextUpload = "Next: Upload another file";
    w_TranslationJobConfirmTranslate = "Confirm & Translate";
    w_TranslationJobCreating = "Creating...";
    w_TranslationJobConfirmFailed = "Failed to create translation job.";
    w_LangAuto = "Auto Detect";
    w_LangEnglish = "English";
    w_LangJapanese = "Japanese";
    w_LangTraditionalChinese = "Chinese (Traditional)";
    w_LangSimplifiedChinese = "Chinese (Simplified)";
    w_LangFrench = "French";
    w_LangThai = "Thai";
    w_LangIndonesian = "Indonesian";
    w_LangVietnamese = "Vietnamese";

    /// Others
    w_OK = "OK";
    w_User = "User";
    w_BelongToInstance = "Belong to {0}";
    w_Relations = "Relations";
    w_Filter = "Filter:";
    w_Unlimited = "Unlimited";
    w_Manage = "Manage";
    w_Status = "Status";
    w_StatusActive = "Active";
    w_StatusInactive = "Inactive";
    w_StatusDisabled = "Disabled";
    w_StatusExhausted = "Exhausted";
    w_JoinedDate = "Joined Date";
    w_CreatedAt = "Created At";
    w_SaveChanges = "Save Changes";
    w_LeaveOrganization = "Leave";
    w_ConfirmLeave = "Confirm Leave";
    w_ConfirmUnlink = "Confirm Unlink";

    // Title
    w_TitleSub1 = "A New Standard for AI Translation, Elevating Efficiency and Success";
    w_TitleSub2 = "Quality";
    w_TitleSub3 =
        "Professionally trained on documents to deliver translation accuracy beyond market standards.";
    w_TitleSub4 = "Uncompromised Format";
    w_TitleSub5 =
        "Focused on layout fidelity to ensure presentations, tables, and document formatting remain intact after translation, eliminating rework.";
    w_TitleSub6 = "Abundant File Support";
    w_TitleSub7 =
        "A one-stop solution for all your translation needs, fully supporting mainstream Office formats such as Word, Excel, and PowerPoint.";
    w_TitleSub8 = "Fantastic Efficiency";
    w_TitleSub9 =
        "Save 70% of formatting adjustment time, freeing up your valuable time and productivity.";
    w_TitleSub10 = "Sign in to your account";
    w_TitleSub11 = "Choose any method to continue";
    w_TitleSub12 = "Sign in with Google";
    w_TitleSub13 = "Local Sign In";
    w_TitleSub14 = "Enterprise Sign In";
    w_TitleSub15 = "Stay signed in";
    w_TitleSub16 = "Account";
    w_TitleSub17 = "LDAP Account";
    w_TitleSub18 = "Forgot password?";
    w_LoginUsernamePlaceholder = "Email or account";
    w_LoginPasswordPlaceholder = "Password";
    w_LoginLdapPlaceholder = "LDAP account";
    w_LoginLdapPasswordPlaceholder = "LDAP password";

    /// Error handler
    w_DataDuplicate = "Duplicate data";
    e_InvalidCurrentPassword =
        "The current password is incorrect, unable to update the user password.";
    e_UNIQUE_CONSTRAINT = "Duplicate data detected. Please check and try again.";
    e_UNIQUE_CONSTRAINT_FIELD =
        "<b class='text-info'>{0}</b> already exists. Please use another value.";
}
